/**
 * SessionsPanel — inline visit-by-visit tracker for a single Treatment.
 *
 * Mounted below a treatment row on the Treatments page. Handles:
 *  - Listing existing sessions with status, stage, doctor, date
 *  - Adding a new planned session
 *  - Completing a session with an "Additional consumables" picker
 *  - Cancelling a planned session
 *
 * Inventory rules (backend enforced):
 *  - Session creation NEVER deducts stock.
 *  - Session completion:
 *      • Fires the parent treatment's kit once (idempotent — only the FIRST
 *        completed session triggers the deduction).
 *      • Deducts each session's own additional_consumables fresh.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Plus, CheckCircle2, XCircle, Loader2, Search, Package, ChevronRight,
  Trash2, ClipboardList, Stethoscope,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Time24Input } from "@/components/Time24Input";
import { DateDMYInput } from "@/components/DateDMYInput";

const STATUS_STYLES = {
  scheduled:   "bg-[#FDF3ED] text-[#8A3F1E]",
  planned:     "bg-linen text-ink",
  in_progress: "bg-[#FDF3ED] text-[#D06A42]",
  completed:   "bg-[#E8F0EB] text-[#2D6A4F]",
  cancelled:   "bg-[#F5E7E4] text-[#8A2D2D]",
  missed:      "bg-[#F5E7E4] text-[#8A2D2D]",
};

const STATUS_DOT = {
  scheduled:   "bg-[#D06A42]",
  planned:     "bg-[#C5B58C]",
  in_progress: "bg-[#D06A42]",
  completed:   "bg-[#2D6A4F]",
  cancelled:   "bg-[#8A2D2D]",
  missed:      "bg-[#8A2D2D]",
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function SessionsPanel({ treatment, onSessionChange }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [completeSession, setCompleteSession] = useState(null);
  const [chairs, setChairs] = useState([]);
  const [team, setTeam] = useState([]);
  // Reasonable defaults: today's date, next round hour.
  const today = new Date();
  const defaultDate = today.toISOString().slice(0, 10);
  const defaultTime = `${String((today.getHours() + 1) % 24).padStart(2, "0")}:00`;
  const [addForm, setAddForm] = useState({
    stage_name: "", notes: "", internal_notes: "",
    date: defaultDate, time: defaultTime, duration_min: 30,
    chair_id: "", doctor_id: "", status: "scheduled",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, t] = await Promise.all([
        api.get(`/treatment-sessions?treatment_id=${treatment.id}`),
        api.get(`/chairs`).catch(() => ({ data: [] })),
        api.get(`/team`).catch(() => ({ data: [] })),
      ]);
      setSessions(s.data);
      setChairs(c.data || []);
      setTeam(t.data || []);
    } catch (err) {
      toast.error(apiError(err, "Failed to load sessions"));
    } finally { setLoading(false); }
  }, [treatment.id]);
  useEffect(() => { load(); }, [load]);

  // Compose ISO datetime from date + time inputs (local tz), then rely on
  // backend to validate working hours / chair overlap.
  const composeStart = (date, time) => {
    if (!date || !time) return null;
    const [h, m] = time.split(":").map(Number);
    const d = new Date(date + "T00:00:00");
    d.setHours(h || 0, m || 0, 0, 0);
    return d.toISOString();
  };

  const createSession = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const start = composeStart(addForm.date, addForm.time);
      await api.post("/treatment-sessions", {
        treatment_id: treatment.id,
        stage_name: addForm.stage_name.trim() || null,
        notes: addForm.notes.trim() || null,
        internal_notes: addForm.internal_notes.trim() || null,
        doctor_id: addForm.doctor_id || null,
        chair_id: addForm.chair_id || null,
        start: start,
        duration_min: Number(addForm.duration_min) || 30,
        status: addForm.status || "scheduled",
      });
      toast.success(start ? "Session scheduled — appointment booked" : "Session added");
      setAddForm({
        stage_name: "", notes: "", internal_notes: "",
        date: defaultDate, time: defaultTime, duration_min: 30,
        chair_id: "", doctor_id: "", status: "scheduled",
      });
      setAddOpen(false);
      await load();
      onSessionChange?.();
    } catch (err) {
      toast.error(apiError(err, "Failed"));
    } finally { setSaving(false); }
  };

  const cancelSession = async (s) => {
    const reason = window.prompt("Cancel this session? Optionally enter a reason:", "");
    if (reason === null) return;
    try {
      await api.post(`/treatment-sessions/${s.id}/cancel`, { reason });
      toast.success("Session cancelled");
      await load();
      onSessionChange?.();
    } catch (err) {
      toast.error(apiError(err, "Failed"));
    }
  };

  const deleteSession = async (s) => {
    if (!window.confirm("Delete this planned session? This cannot be undone.")) return;
    try {
      await api.delete(`/treatment-sessions/${s.id}`);
      toast.success("Session deleted");
      await load();
      onSessionChange?.();
    } catch (err) {
      toast.error(apiError(err, "Failed"));
    }
  };

  const completedCount = sessions.filter((s) => s.status === "completed").length;
  const plannedCount = sessions.filter((s) => s.status === "planned").length;

  return (
    <div className="bg-[#FAFAF6] border-t border-[#EFEADC] px-6 py-5" data-testid={`sessions-panel-${treatment.id}`}>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="overline text-ink-soft flex items-center gap-2">
            <ClipboardList className="w-3.5 h-3.5" /> Visit history
          </div>
          <div className="text-xs text-ink-mid mt-1">
            {sessions.length === 0 ? "No visits scheduled yet." :
              <>
                <span data-testid="sess-count-completed">{completedCount} completed</span>
                {plannedCount > 0 && <> · <span data-testid="sess-count-planned">{plannedCount} planned</span></>}
              </>}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          data-testid={`btn-add-session-${treatment.id}`}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pine text-linen text-xs font-medium hover:bg-[#264A3B] transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add session
        </button>
      </div>

      {loading ? (
        <div className="py-6 text-center text-xs text-ink-soft flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading sessions…
        </div>
      ) : sessions.length === 0 ? (
        <div className="py-6 text-center text-xs text-ink-soft border border-dashed border-[#E8EAE9] rounded-2xl bg-white">
          Add the first session when the patient is ready to start.
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map((s) => (
            <div
              key={s.id}
              data-testid={`session-row-${s.id}`}
              className="rounded-2xl bg-white border border-[#E8EAE9] p-3 flex items-center gap-3 flex-wrap"
            >
              {/* Timeline bullet — coloured by status */}
              <div className="flex flex-col items-center shrink-0">
                <div className={`w-3 h-3 rounded-full ${STATUS_DOT[s.status] || "bg-[#C5B58C]"} ring-4 ring-white shadow-sm`} />
                <div className="text-[10px] font-mono text-ink-soft mt-1">#{s.visit_number}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-medium text-ink truncate">
                    {s.stage_name || `Visit ${s.visit_number}`}
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest ${STATUS_STYLES[s.status] || "bg-linen text-ink"}`}>
                    {(s.status || "").replace("_", " ")}
                  </span>
                </div>
                <div className="text-[11px] text-ink-mid mt-0.5 flex items-center gap-2 flex-wrap">
                  {s.start && (
                    <span data-testid={`session-when-${s.id}`} className="inline-flex items-center gap-1 font-mono">
                      {fmtDateTime(s.start)}{s.duration_min ? ` · ${s.duration_min}m` : ""}
                    </span>
                  )}
                  {s.doctor_name && (<span className="inline-flex items-center gap-1"><Stethoscope className="w-3 h-3" /> {s.doctor_name}</span>)}
                  {s.chair_id && (
                    <span className="inline-flex items-center gap-1">
                      · {chairs.find((c) => c.id === s.chair_id)?.name || "Chair"}
                    </span>
                  )}
                  {s.completed_at && <span>Completed {fmtDateTime(s.completed_at)}</span>}
                  {s.status === "completed" && (
                    s.no_kit_used
                      ? <span className="inline-flex items-center gap-1 text-[#8A3F1E]"><Package className="w-3 h-3" /> No kit used</span>
                      : s.kit_name && <span className="inline-flex items-center gap-1"><Package className="w-3 h-3" /> {s.kit_name}</span>
                  )}
                  {s.status === "completed" && s.material_cost > 0 && (
                    <span className="text-ink-mid">Materials ₹{s.material_cost.toLocaleString("en-IN")}</span>
                  )}
                  {s.cancel_reason && <span className="italic">· {s.cancel_reason}</span>}
                </div>
                {s.notes && <div className="text-[11px] text-ink-mid mt-1 italic">&ldquo;{s.notes}&rdquo;</div>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {(s.status === "planned" || s.status === "scheduled" || s.status === "in_progress") && (
                  <>
                    <button
                      onClick={() => setCompleteSession(s)}
                      data-testid={`btn-complete-session-${s.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#E8F0EB] text-[#2D6A4F] text-[11px] font-medium hover:bg-[#D8E6DC] transition-colors"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                    </button>
                    <button
                      onClick={() => cancelSession(s)}
                      data-testid={`btn-cancel-session-${s.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-[#E8EAE9] text-ink-mid text-[11px] hover:bg-linen transition-colors"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => deleteSession(s)}
                      data-testid={`btn-delete-session-${s.id}`}
                      className="inline-flex items-center gap-1 p-1 rounded-full border border-[#F0A5A5] text-[#8B1E1E] hover:bg-[#FFF3F3] transition-colors"
                      title="Delete planned/scheduled session"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add-session dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Add session</DialogTitle>
          </DialogHeader>
          <form onSubmit={createSession} className="space-y-3" data-testid="add-session-form">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">
                Session name
              </label>
              <input
                data-testid="sess-stage-name"
                placeholder="e.g. Consultation, RCT Visit 1, Implant Placement, Crown Try-in"
                value={addForm.stage_name}
                onChange={(e) => setAddForm({ ...addForm, stage_name: e.target.value })}
                className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Appointment date *</label>
                <DateDMYInput required
                  data-testid="sess-date"
                  value={addForm.date}
                  onChange={(e) => setAddForm({ ...addForm, date: e.target.value })}
                  className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Time *</label>
                <Time24Input
                  data-testid="sess-time"
                  value={addForm.time}
                  onChange={(v) => setAddForm({ ...addForm, time: v })}
                  className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Duration</label>
                <select
                  data-testid="sess-duration"
                  value={addForm.duration_min}
                  onChange={(e) => setAddForm({ ...addForm, duration_min: e.target.value })}
                  className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white"
                >
                  {[15, 30, 45, 60, 90, 120].map((m) => (
                    <option key={m} value={m}>{m} min</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Status</label>
                <select
                  data-testid="sess-status"
                  value={addForm.status}
                  onChange={(e) => setAddForm({ ...addForm, status: e.target.value })}
                  className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white"
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="planned">Planned (no date)</option>
                  <option value="in_progress">In progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="missed">Missed</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Chair</label>
                <select
                  data-testid="sess-chair"
                  value={addForm.chair_id}
                  onChange={(e) => setAddForm({ ...addForm, chair_id: e.target.value })}
                  className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white"
                >
                  <option value="">Auto</option>
                  {chairs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Assigned doctor</label>
                <select
                  data-testid="sess-doctor"
                  value={addForm.doctor_id}
                  onChange={(e) => setAddForm({ ...addForm, doctor_id: e.target.value })}
                  className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white"
                >
                  <option value="">Treating doctor</option>
                  {team.filter((t) => t.role === "doctor").map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Clinical notes</label>
              <textarea
                data-testid="sess-notes"
                placeholder="Clinical notes for this visit"
                value={addForm.notes}
                onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                rows={2}
                className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Internal notes (optional)</label>
              <input
                data-testid="sess-internal-notes"
                placeholder="Team-only reminders"
                value={addForm.internal_notes}
                onChange={(e) => setAddForm({ ...addForm, internal_notes: e.target.value })}
                className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm"
              />
            </div>

            <div className="text-[11px] text-ink-mid bg-linen/60 rounded-xl px-3 py-2">
              A Calendar appointment is created automatically and the selected doctor + chair are reserved. Inventory only moves when the session is marked complete.
            </div>
            <button
              data-testid="sess-submit"
              disabled={saving}
              className="w-full btn-primary text-sm inline-flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {saving ? "Saving…" : "Add session"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Complete-session dialog */}
      {completeSession && (
        <CompleteSessionDialog
          session={completeSession}
          treatment={treatment}
          onClose={() => setCompleteSession(null)}
          onDone={async () => {
            setCompleteSession(null);
            await load();
            onSessionChange?.();
          }}
        />
      )}
    </div>
  );
}


/* ----- CompleteSessionDialog: kit picker + additional consumables + confirm ----- */
function CompleteSessionDialog({ session, treatment: _treatment, onClose, onDone }) {
  const [kits, setKits] = useState([]);
  const [kitId, setKitId] = useState(null); // null = "No Kit Used"
  const [noKitUsed, setNoKitUsed] = useState(false);
  const [kitQ, setKitQ] = useState("");
  const [kitOpen, setKitOpen] = useState(false);
  const [suggested, setSuggested] = useState(null); // {kit_id, kit_name, reason}
  const [suggestionApplied, setSuggestionApplied] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [items, setItems] = useState([]); // extras
  const [q, setQ] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingInv, setLoadingInv] = useState(true);
  const [loadingKits, setLoadingKits] = useState(true);

  // Load active kits + kit suggestion + inventory in parallel
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: kitList }, { data: sug }, { data: inv }] = await Promise.all([
          api.get("/treatment-kits?active_only=true"),
          api.get(`/treatment-sessions/${session.id}/kit-suggestion`).catch(() => ({ data: null })),
          api.get("/inventory/items?limit=500").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setKits(kitList || []);
        setInventory(inv || []);
        if (sug && sug.kit_id) {
          setSuggested(sug);
          setKitId(sug.kit_id);
          setSuggestionApplied(true);
        }
      } catch {
        // fall through silently — user can still pick manually
      } finally {
        if (!cancelled) { setLoadingInv(false); setLoadingKits(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [session.id]);

  const selectedKit = useMemo(
    () => kits.find((k) => k.id === kitId) || null,
    [kits, kitId],
  );

  const filteredKits = useMemo(() => {
    const query = kitQ.trim().toLowerCase();
    if (!query) return kits;
    return kits.filter((k) =>
      (k.name || "").toLowerCase().includes(query) ||
      (k.procedure || "").toLowerCase().includes(query) ||
      (k.variant || "").toLowerCase().includes(query),
    );
  }, [kits, kitQ]);

  const pickKit = (kit) => {
    setKitId(kit.id);
    setNoKitUsed(false);
    setKitOpen(false);
    setKitQ("");
    setSuggestionApplied(false);
  };

  const pickNoKit = () => {
    setKitId(null);
    setNoKitUsed(true);
    setKitOpen(false);
    setKitQ("");
    setSuggestionApplied(false);
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return inventory.slice(0, 8);
    return inventory
      .filter((i) => !i.is_deleted && (
        (i.name || "").toLowerCase().includes(query) ||
        (i.sku || "").toLowerCase().includes(query)
      ))
      .slice(0, 10);
  }, [inventory, q]);

  const addItem = (inv) => {
    if (items.find((it) => it.item_id === inv.id)) {
      toast.info("Already added");
      return;
    }
    setItems((prev) => [...prev, {
      item_id: inv.id, qty: 1, name: inv.name, unit: inv.unit, unit_cost: inv.unit_cost, stock: inv.stock,
    }]);
    setQ("");
  };

  const updateQty = (id, qty) => {
    setItems((prev) => prev.map((it) => it.item_id === id ? { ...it, qty } : it));
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((it) => it.item_id !== id));
  };

  const submit = async () => {
    // Guard: doctor must explicitly pick a kit or confirm "No Kit Used"
    if (kitId === null && !noKitUsed) {
      toast.error("Select a treatment kit or choose 'No Kit Used'");
      return;
    }
    for (const it of items) {
      if (!it.qty || Number(it.qty) <= 0) {
        toast.error(`Enter a valid quantity for ${it.name}`);
        return;
      }
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/treatment-sessions/${session.id}/complete`, {
        kit_id: kitId,
        no_kit_used: noKitUsed,
        notes: notes.trim() || null,
        additional_consumables: items.map((it) => ({
          item_id: it.item_id, qty: Number(it.qty),
        })),
      });
      const kit = data.kit_result || {};
      const extras = data.additional_result || {};
      const consumedCount = (kit.consumed_items?.length || 0) + (extras.consumed_items?.length || 0);
      const materialCost = data.material_cost || 0;
      if (noKitUsed) {
        toast.success(
          consumedCount
            ? `Session completed · No kit · ${consumedCount} additional item${consumedCount > 1 ? "s" : ""} deducted (₹${materialCost.toLocaleString("en-IN")})`
            : "Session completed · No kit or consumables recorded",
          { duration: 4500 },
        );
      } else if (kit.auto_deduct_disabled) {
        toast.success(`Session completed · Kit '${kit.kit_name}' has auto-deduct off`, { duration: 4500 });
      } else if (kit.consumed_items?.length) {
        toast.success(
          `Session completed · ${kit.kit_name} + ${consumedCount - (kit.consumed_items?.length || 0)} extras · ₹${materialCost.toLocaleString("en-IN")}`,
          { duration: 4500 },
        );
      } else {
        toast.success("Session completed");
      }
      (extras.low_stock_warnings || []).concat(kit.low_stock_warnings || []).forEach((w) =>
        toast.warning(
          `Low stock: ${w.item_name} · ${w.new_stock} ${w.unit} left (reorder at ${w.reorder_level})`,
          { duration: 6000 },
        ),
      );
      onDone();
    } catch (err) {
      toast.error(apiError(err, "Failed to complete session"));
    } finally { setSaving(false); }
  };

  const extrasTotal = items.reduce((sum, it) => sum + (Number(it.qty) || 0) * (it.unit_cost || 0), 0);
  const kitCost = useMemo(() => {
    if (!selectedKit) return 0;
    return (selectedKit.items || []).reduce(
      (s, i) => s + Number(i.qty || 0) * Number(i.unit_cost || 0), 0,
    );
  }, [selectedKit]);

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-white max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            Complete visit #{session.visit_number}
            {session.stage_name && <span className="text-ink-mid text-base"> · {session.stage_name}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4" data-testid="complete-session-dialog">
          {/* Treatment Kit selector */}
          <div>
            <div className="overline text-ink-soft mb-1 flex items-center gap-2">
              <Package className="w-3 h-3" /> Treatment Kit Used Today
            </div>

            {/* Trigger button */}
            <button
              type="button"
              onClick={() => setKitOpen((v) => !v)}
              disabled={loadingKits}
              data-testid="kit-selector-trigger"
              className={`w-full text-left px-4 py-3 rounded-2xl border transition-colors ${
                kitOpen ? "border-pine bg-white" : "border-[#E8EAE9] bg-white hover:border-pine/40"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  {loadingKits ? (
                    <span className="text-sm text-ink-soft inline-flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading kits…
                    </span>
                  ) : selectedKit ? (
                    <div>
                      <div className="text-sm font-medium text-ink truncate">{selectedKit.name}</div>
                      <div className="text-[10px] text-ink-soft truncate">
                        {selectedKit.procedure}{selectedKit.variant ? ` · ${selectedKit.variant}` : ""} · {(selectedKit.items || []).length} items · ₹{kitCost.toLocaleString("en-IN")}
                      </div>
                    </div>
                  ) : noKitUsed ? (
                    <div className="text-sm font-medium text-[#8A3F1E]">No Kit Used</div>
                  ) : (
                    <div className="text-sm text-ink-soft">Select a kit or &ldquo;No Kit Used&rdquo;</div>
                  )}
                </div>
                <ChevronRight className={`w-4 h-4 text-ink-soft transition-transform shrink-0 ${kitOpen ? "rotate-90" : ""}`} />
              </div>
            </button>

            {/* Suggestion hint */}
            {suggested && suggestionApplied && (
              <div className="text-[11px] text-ink-mid mt-1 italic" data-testid="kit-suggested-hint">
                Auto-suggested for this visit &mdash; you can change it below.
              </div>
            )}

            {/* Dropdown panel */}
            {kitOpen && (
              <div className="mt-2 border border-[#E8EAE9] bg-white rounded-2xl overflow-hidden shadow-sm" data-testid="kit-dropdown-panel">
                <div className="relative border-b border-[#E8EAE9]">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
                  <input
                    autoFocus
                    data-testid="kit-search"
                    value={kitQ}
                    onChange={(e) => setKitQ(e.target.value)}
                    placeholder="Search kits by name, procedure, variant…"
                    className="w-full pl-9 pr-3 py-2 text-sm border-0 focus:outline-none"
                  />
                </div>
                <button
                  type="button"
                  onClick={pickNoKit}
                  data-testid="kit-option-none"
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#FFF6EF] border-b border-[#F0EBE0] flex items-center gap-3 ${
                    noKitUsed ? "bg-[#FDF3ED] text-[#8A3F1E]" : "text-ink/80"
                  }`}
                >
                  <span className="w-4 h-4 rounded-full border border-[#D6C6B5] grid place-items-center">
                    {noKitUsed && <span className="w-2 h-2 rounded-full bg-[#8A3F1E]" />}
                  </span>
                  <span className="font-medium">No Kit Used</span>
                  <span className="text-[10px] text-ink-soft">· Review visit / no consumption</span>
                </button>
                <div className="max-h-60 overflow-auto">
                  {filteredKits.length === 0 ? (
                    <div className="py-4 text-center text-xs text-ink-soft">
                      {kits.length === 0 ? "No active kits configured. Ask admin to create one." : "No matches."}
                    </div>
                  ) : filteredKits.map((k) => {
                    const isSuggested = suggested?.kit_id === k.id;
                    const isSelected = kitId === k.id;
                    return (
                      <button
                        type="button"
                        key={k.id}
                        onClick={() => pickKit(k)}
                        data-testid={`kit-option-${k.id}`}
                        className={`w-full text-left px-4 py-2 text-sm border-b border-[#F0EBE0] last:border-b-0 flex items-center gap-3 hover:bg-linen/60 ${
                          isSelected ? "bg-linen" : ""
                        }`}
                      >
                        <span className="w-4 h-4 rounded-full border border-[#D6C6B5] grid place-items-center shrink-0">
                          {isSelected && <span className="w-2 h-2 rounded-full bg-pine" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="font-medium text-ink block truncate">{k.name}</span>
                          <span className="text-[10px] text-ink-soft block truncate">
                            {k.procedure}{k.variant ? ` · ${k.variant}` : ""} · {(k.items || []).length} items
                          </span>
                        </span>
                        {isSuggested && (
                          <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#E8F0EB] text-[#2D6A4F] shrink-0">
                            Suggested
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Kit preview */}
            {selectedKit && !kitOpen && (selectedKit.items || []).length > 0 && (
              <div className="mt-2 rounded-2xl bg-linen/50 border border-[#EFEADC] p-3">
                <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1.5">Kit will deduct</div>
                <div className="space-y-0.5">
                  {selectedKit.items.map((i) => (
                    <div key={i.item_id} className="text-[11px] text-ink-mid flex justify-between gap-2">
                      <span className="truncate">{i.item_name || "(item)"}</span>
                      <span className="font-mono shrink-0">{i.qty} {i.unit}{i.current_stock < i.qty ? " · low stock" : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Additional consumables picker */}
          <div>
            <div className="overline text-ink-soft mb-1">Additional consumables (optional)</div>
            <p className="text-[11px] text-ink-mid mb-2">
              Anything used today that wasn&rsquo;t part of the selected kit — anaesthetic, extra composite, sutures, etc.
            </p>

            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input
                data-testid="extras-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={loadingInv ? "Loading inventory…" : "Search inventory to add…"}
                disabled={loadingInv}
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-[#E8EAE9] text-sm"
              />
              {q && filtered.length > 0 && (
                <div className="absolute z-10 mt-1 left-0 right-0 bg-white border border-[#E8EAE9] rounded-xl shadow-lg max-h-56 overflow-auto"
                     data-testid="extras-search-results">
                  {filtered.map((inv) => (
                    <button
                      key={inv.id} type="button"
                      onClick={() => addItem(inv)}
                      data-testid={`extras-pick-${inv.id}`}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-linen flex items-center justify-between gap-2 border-b border-[#F0EBE0] last:border-b-0"
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-ink truncate">{inv.name}</div>
                        <div className="text-[10px] text-ink-soft">Stock {inv.stock} {inv.unit} · ₹{inv.unit_cost}/{inv.unit}</div>
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 text-ink-soft shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid="extras-selected-list">
                {items.map((it) => (
                  <div key={it.item_id} data-testid={`extras-row-${it.item_id}`}
                       className="flex items-center gap-2 rounded-xl bg-white border border-[#E8EAE9] px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink truncate">{it.name}</div>
                      <div className="text-[10px] text-ink-soft">Stock {it.stock} {it.unit}</div>
                    </div>
                    <input
                      type="number" min="0.01" step="0.01"
                      value={it.qty}
                      onChange={(e) => updateQty(it.item_id, e.target.value)}
                      data-testid={`extras-qty-${it.item_id}`}
                      className="w-20 text-sm rounded-lg border border-[#E8EAE9] px-2 py-1 text-right font-mono"
                    />
                    <span className="text-[11px] text-ink-mid w-10">{it.unit}</span>
                    <button
                      type="button" onClick={() => removeItem(it.item_id)}
                      data-testid={`extras-remove-${it.item_id}`}
                      className="text-ink-soft hover:text-[#8B1E1E]" aria-label="Remove">
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="text-[11px] text-ink-mid text-right">
                  Extras subtotal · <b>₹{extrasTotal.toLocaleString("en-IN")}</b>
                </div>
              </div>
            )}
          </div>

          {/* Session notes */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Session notes (optional)</label>
            <textarea
              data-testid="complete-notes"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="What was done at this visit"
              className="w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm"
            />
          </div>

          <div className="flex items-center gap-2 justify-end">
            <button
              type="button" onClick={onClose} data-testid="complete-cancel"
              className="text-xs px-4 py-2 rounded-full border border-[#E8EAE9] text-ink-mid hover:bg-linen"
            >
              Cancel
            </button>
            <button
              type="button" onClick={submit} disabled={saving}
              data-testid="complete-confirm"
              className="text-xs px-4 py-2 rounded-full bg-pine text-linen font-medium hover:bg-[#264A3B] inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {saving ? "Completing…" : "Complete session"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
